import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLite, LITE_PHASES } from "./context/LiteContext";

const AutoRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, loading: authLoading } = useAuth();
  const {
    liteJourney,
    currentDay,
    completedDays,
    phase,
    loading: liteLoading,
  } = useLite();

  useEffect(() => {
    if (authLoading || liteLoading) return;

    const path = location.pathname;

    /* =================================================
       🟢 ADMIN BYPASS
    ================================================= */
    if (user?.role === "admin") {
      if (path === "/") {
        navigate("/admin-dashboard", { replace: true });
      }
      return;
    }

    /* =================================================
       🚫 DO NOT INTERFERE WITH AUTH ROUTES
    ================================================= */
    if (path.startsWith("/login") || path.startsWith("/register")) {
      return;
    }

    /* =================================================
       🚫 DO NOT INTERFERE WITH TRAINING
    ================================================= */
    if (path.startsWith("/training")) return;

    /* =================================================
       ⭐ PRO CHECK
    ================================================= */
    const isPro =
      user?.plan === "pro" || user?.role === "admin";

    /* =================================================
       🔴 UPGRADE INTENT (ONE-SHOT)
    ================================================= */
    const upgradeIntent = localStorage.getItem("post_auth_intent");
    const intentConsumed = sessionStorage.getItem(
      "upgrade_intent_consumed"
    );

    if (user && upgradeIntent === "pro-upgrade" && !intentConsumed) {
      sessionStorage.setItem("upgrade_intent_consumed", "true");
      localStorage.removeItem("post_auth_intent");
      localStorage.removeItem("lite_resume_step");

      if (isPro) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/pricing", { replace: true });
      }
      return;
    }

    /* =================================================
       🏠 ROOT ROUTE HANDLING
    ================================================= */
    if (path === "/") {
      if (!user) {
        navigate("/lite/welcome", { replace: true });
        return;
      }

      if (isPro) {
        navigate("/dashboard", { replace: true });
        return;
      }

      navigate("/lite/welcome", { replace: true });
      return;
    }

    /* =================================================
       👤 GUEST USER (SAFE)
    ================================================= */
    if (!user) {
      // Only redirect if outside lite
      if (!path.startsWith("/lite")) {
        navigate("/lite/welcome", { replace: true });
      }
      return;
    }

    /* =================================================
       💎 PRO USER PROTECTION
    ================================================= */
    if (isPro) {
      if (path.startsWith("/lite")) {
        navigate("/dashboard", { replace: true });
      }
      return;
    }

    /* =================================================
       🌱 LITE USER — JOURNEY SAFETY
    ================================================= */
    if (!liteJourney) {
      if (!path.startsWith("/lite")) {
        navigate("/lite/welcome", { replace: true });
      }
      return;
    }

    /* =================================================
       🧭 DAY GUARD (OPTIONAL SAFETY)
    ================================================= */
    if (path.startsWith("/lite/day/")) {
      const requestedDay = Number(path.split("/lite/day/")[1]);

      if (
        Number.isFinite(requestedDay) &&
        requestedDay > currentDay
      ) {
        navigate(`/lite/day/${currentDay}`, { replace: true });
        return;
      }
    }

    /* =================================================
       🔁 LEGACY RESUME (WELCOME ONLY)
    ================================================= */
    const liteResumeStep = localStorage.getItem("lite_resume_step");

    if (liteResumeStep && path === "/lite/welcome") {
      const resumeMap = {
        "market-potential": "/lite/market-potential",
        "direction-confirm": "/lite/direction-confirm",
        idea: "/lite/idea",
        purpose: "/lite/purpose",
      };

      const resumePath = resumeMap[liteResumeStep];
      if (resumePath) {
        navigate(resumePath, { replace: true });
        return;
      }
    }

    /* =================================================
       🚀 UPGRADE TRIGGER (ONLY WHEN READY)
    ================================================= */
    if (
      phase === LITE_PHASES.ELIGIBLE_FOR_PRO &&
      completedDays?.length >= 21
    ) {
      if (path !== "/lite/upgrade") {
        navigate("/lite/upgrade", { replace: true });
      }
      return;
    }

  }, [
    user,
    liteJourney,
    currentDay,
    completedDays,
    phase,
    authLoading,
    liteLoading,
    location.pathname,
    navigate,
  ]);

  return null;
};

export default AutoRedirect;